import { Component } from '@theme/component';
import { debounce, onAnimationEnd, prefersReducedMotion, onDocumentLoaded } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { DialogCloseEvent, DialogComponent } from '@theme/dialog';
import { publishSearchUsed } from '@theme/search-tracking';

/**
 * A custom element that allows the user to search for resources available on the store.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLElement} predictiveSearchResults - The predictive search results container.
 * @property {HTMLElement} resetButton - The reset button element.
 * @property {HTMLElement[]} [resultsItems] - The search results items elements.
 * @property {HTMLElement} [recentlyViewedWrapper] - The recently viewed products wrapper.
 * @property {HTMLElement[]} [recentlyViewedTitle] - The recently viewed title elements.
 * @property {HTMLElement[]} [recentlyViewedItems] - The recently viewed product items.
 * @property {HTMLElement} [searchInputTyped] - The ghost-text span showing the typed prefix.
 * @property {HTMLElement} [searchInputSuggestion] - The ghost-text span showing the suggested completion.
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton'];

  #controller = new AbortController();

  /**
   * Popular search suggestions (from the theme's `search_suggestions` setting), used as the
   * primary source for the ghost-text autocomplete completion before falling back to the top
   * query suggestion returned by the predictive search response.
   * @type {string[]}
   */
  #popularSuggestions = (this.dataset.suggestions || '').split('|').map((term) => term.trim()).filter(Boolean);

  /**
   * The most recent top query suggestion text returned by the predictive search response,
   * used as a fallback for the ghost-text autocomplete completion.
   * @type {string | null}
   */
  #lastQuerySuggestion = null;

  /**
   * @type {AbortController | null}
   */
  #activeFetch = null;

  /**
   * Get the dialog component.
   * @returns {DialogComponent | null} The dialog component.
   */
  get dialog() {
    return this.closest('dialog-component');
  }

  connectedCallback() {
    super.connectedCallback();

    const { dialog } = this;
    const { signal } = this.#controller;

    if (this.refs.searchInput.value.length > 0) {
      this.#showResetButton();
    }

    if (dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });

      this.addEventListener('click', this.#handleModalClick, { signal });
    }

    onDocumentLoaded(() => {
      this.resetSearch(false); // Pass false to avoid focusing the input
    });
  }

  /**
   * Handles clicks within the predictive search modal to maintain focus on the input
   * @param {MouseEvent} event - The mouse event
   */
  #handleModalClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const isInteractiveElement =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input');

    if (!isInteractiveElement && this.refs.searchInput) {
      this.refs.searchInput.focus();
    }
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * Handles the CMD+K key combination.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === 'k') {
      this.dialog?.toggleDialog();
    }
  };

  /**
   * Handles the dialog close event.
   */
  #handleDialogClose = () => {
    this.#resetSearch();
  };

  get #allResultsItems() {
    const containers = Array.from(
      this.querySelectorAll(
        '.predictive-search-results__wrapper-queries, ' +
          '.predictive-search-results__wrapper-products, ' +
          '.predictive-search-results__list'
      )
    );

    const allItems = containers
      .flatMap((container) => {
        if (container.classList.contains('predictive-search-results__wrapper-products')) {
          return Array.from(container.querySelectorAll('.predictive-search-results__card'));
        }
        return Array.from(container.querySelectorAll('[ref="resultsItems[]"], .predictive-search-results__card'));
      })
      .filter((item) => item instanceof HTMLElement);

    return /** @type {HTMLElement[]} */ (allItems);
  }

  /**
   * Track whether the last interaction was keyboard-based
   * @type {boolean}
   */
  #isKeyboardNavigation = false;

  get #currentIndex() {
    return this.#allResultsItems?.findIndex((item) => item.getAttribute('aria-selected') === 'true') ?? -1;
  }

  set #currentIndex(index) {
    if (!this.#allResultsItems?.length) return;

    this.#allResultsItems.forEach((item) => {
      item.classList.remove('keyboard-focus');
    });

    for (const [itemIndex, item] of this.#allResultsItems.entries()) {
      if (itemIndex === index) {
        item.setAttribute('aria-selected', 'true');

        if (this.#isKeyboardNavigation) {
          item.classList.add('keyboard-focus');
        }
        item.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'nearest' });
      } else {
        item.removeAttribute('aria-selected');
      }
    }
    this.refs.searchInput.focus();
  }

  get #currentItem() {
    return this.#allResultsItems?.[this.#currentIndex];
  }

  /**
   * Navigate through the predictive search results using arrow keys or close them with the Escape key.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  onSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      this.#resetSearch();
      return;
    }

    if (!this.#allResultsItems?.length || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      return;
    }

    const currentIndex = this.#currentIndex;
    const totalItems = this.#allResultsItems.length;

    switch (event.key) {
      case 'ArrowDown':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        break;

      case 'Tab':
        if (event.shiftKey) {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        } else {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        }
        break;

      case 'ArrowUp':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        break;

      case 'Enter': {
        const singleResultContainer = this.refs.predictiveSearchResults.querySelector('[data-single-result-url]');
        if (singleResultContainer instanceof HTMLElement && singleResultContainer.dataset.singleResultUrl) {
          event.preventDefault();
          window.location.href = singleResultContainer.dataset.singleResultUrl;
          return;
        }

        if (this.#currentIndex >= 0) {
          event.preventDefault();
          this.#currentItem?.querySelector('a')?.click();
        } else {
          const searchUrl = new URL(Theme.routes.search_url, location.origin);
          searchUrl.searchParams.set('q', this.refs.searchInput.value);
          window.location.href = searchUrl.toString();
        }
        break;
      }
    }
  };

  /**
   * Clears the recently viewed products.
   * @param {Event} event - The event.
   */
  clearRecentlyViewedProducts(event) {
    event.stopPropagation();

    RecentlyViewed.clearProducts();

    const { recentlyViewedItems, recentlyViewedTitle, recentlyViewedWrapper } = this.refs;

    const allRecentlyViewedElements = [...(recentlyViewedItems || []), ...(recentlyViewedTitle || [])];

    if (allRecentlyViewedElements.length === 0) {
      return;
    }

    if (recentlyViewedWrapper) {
      recentlyViewedWrapper.classList.add('removing');

      onAnimationEnd(recentlyViewedWrapper, () => {
        recentlyViewedWrapper.remove();
      });
    }
  }

  /**
   * Reset the search state.
   * @param {boolean} [keepFocus=true] - Whether to keep focus on input after reset
   */
  resetSearch = debounce((keepFocus = true) => {
    if (keepFocus) {
      this.refs.searchInput.focus();
    }
    this.#resetSearch();
  }, 100);

  /**
   * Handles input on every keystroke, undebounced. The ghost-text overlay and the native
   * placeholder must stay in lockstep — the placeholder disappears the instant the input has a
   * value (native :placeholder-shown, no debounce), so if the overlay's typed/suggestion spans
   * only updated on the debounced fetch below, there was a ~200ms window after each keystroke
   * where neither showed anything (a visible flash), and the pale typed-text felt laggy while
   * typing continuously. Only the actual network fetch needs debouncing, not this.
   * @param {InputEvent} event - The input event.
   */
  search = (event) => {
    // If the input is not a text input (like using the Escape key), don't search
    if (!event.inputType) return;

    const searchTerm = this.refs.searchInput.value.trim();
    this.#currentIndex = -1;
    this.#updateGhostText(searchTerm);

    if (!searchTerm.length) {
      this.#resetSearch();
      return;
    }

    this.#showResetButton();
    this.#debouncedGetSearchResults(searchTerm);
  };

  /**
   * Debounced fetch/render of predictive search results — the expensive part of #search, kept
   * separate so ghost-text updates aren't held back by the same debounce.
   */
  #debouncedGetSearchResults = debounce((/** @type {string} */ searchTerm) => {
    this.#getSearchResults(searchTerm);
  }, 200);

  /**
   * Updates the ghost-text autocomplete overlay: the typed prefix renders pale grey, and the
   * suggested completion (the remainder after the typed prefix) renders dark/bold. The
   * completion is matched against the merchant's popular-search-suggestions list first,
   * falling back to the top query suggestion from the last predictive search response.
   * @param {string} searchTerm - The current, trimmed input value.
   */
  #updateGhostText(searchTerm) {
    const { searchInputTyped, searchInputSuggestion } = this.refs;
    if (!searchInputTyped || !searchInputSuggestion) return;

    searchInputTyped.textContent = searchTerm;

    if (!searchTerm) {
      searchInputSuggestion.textContent = '';
      return;
    }

    const lowerTerm = searchTerm.toLowerCase();
    const popularMatch = this.#popularSuggestions.find(
      (term) => term.toLowerCase().startsWith(lowerTerm) && term.length > searchTerm.length
    );

    const match =
      popularMatch ||
      (this.#lastQuerySuggestion &&
      this.#lastQuerySuggestion.toLowerCase().startsWith(lowerTerm) &&
      this.#lastQuerySuggestion.length > searchTerm.length
        ? this.#lastQuerySuggestion
        : null);

    searchInputSuggestion.textContent = match ? match.slice(searchTerm.length) : '';
  }

  /**
   * Switches the active results tab (All / Products / Collections / Pages / Articles). All
   * panels are already present in the DOM from the single predictive search fetch — this just
   * toggles which one is visible, no extra request needed.
   * @param {Event} event - The click event from a tab button.
   */
  switchTab(event) {
    // Note: this delegation system dispatches click listeners from `document`, so
    // event.currentTarget is always `document` — the actual clicked/resolved element is
    // event.target (already resolved to the closest [on:click] ancestor by the delegator).
    const target = event.target instanceof HTMLElement ? event.target.closest('[data-tab-target]') : null;
    const tabTarget = target?.dataset.tabTarget;
    if (!tabTarget) return;

    const tabs = this.querySelectorAll('.predictive-search-results__tabs [data-tab-target]');
    tabs.forEach((tab) => {
      const isActive = /** @type {HTMLElement} */ (tab).dataset.tabTarget === tabTarget;
      tab.classList.toggle('predictive-search-results__tab--active', isActive);
      tab.setAttribute('aria-selected', String(isActive));
    });

    const panels = this.querySelectorAll('[data-tab-panel]');
    panels.forEach((panel) => {
      if (!(panel instanceof HTMLElement)) return;
      const isActive = panel.dataset.tabPanel === tabTarget;
      panel.classList.toggle('predictive-search-results__panel--active', isActive);
      panel.hidden = !isActive;
    });
  }

  /**
   * Populates the search input with a suggestion (e.g. from a keyword chip) and triggers a
   * search, matching the behavior of typing the term directly.
   * @param {Event} event - The click event from the suggestion element.
   */
  selectSuggestion(event) {
    // Note: event.currentTarget is always `document` under this delegation system (see
    // #switchTab) — use event.target, which the delegator resolves to the clicked chip.
    const target = event.target instanceof HTMLElement ? event.target.closest('[data-suggestion]') : null;
    const term = target?.dataset.suggestion || target?.textContent?.trim() || '';
    if (!term) return;

    const { searchInput } = this.refs;
    searchInput.value = term;
    searchInput.focus();
    this.#currentIndex = -1;
    this.#updateGhostText(term);
    this.#showResetButton();
    this.#getSearchResults(term);
  }

  /**
   * Resets scroll positions for search results containers
   */
  #resetScrollPositions() {
    requestAnimationFrame(() => {
      const resultsInner = this.refs.predictiveSearchResults.querySelector('.predictive-search-results__inner');
      if (resultsInner instanceof HTMLElement) {
        resultsInner.scrollTop = 0;
      }

      const formContent = this.querySelector('.predictive-search-form__content');
      if (formContent instanceof HTMLElement) {
        formContent.scrollTop = 0;
      }
    });
  }

  /**
   * Fetch search results using the section renderer and update the results container.
   * @param {string} searchTerm - The term to search for
   */
  async #getSearchResults(searchTerm) {
    if (!this.dataset.sectionId) return;

    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    url.searchParams.set('q', searchTerm);
    url.searchParams.set('resources[limit_scope]', 'each');
    // Despite the name, `resources[options][fields]` controls which fields the search QUERY is
    // matched against — it does not limit what's present on the returned product/variant
    // objects (those come from Section Rendering as full Liquid objects regardless, so price/
    // availability/tags were never actually missing). Its values are a fixed Shopify whitelist:
    // author, body, product_type, tag, title, variants.barcode, variants.sku, variants.title,
    // vendor — anything else 422s the request ("Invalid option for `fields` parameter"), which
    // is what including variants.price/variants.compare_at_price/variants.available/tags did.
    url.searchParams.set('resources[options][fields]', 'title,variants.sku,variants.title,vendor,tag,product_type');

    const { predictiveSearchResults } = this.refs;

    const abortController = this.#createAbortController();

    sectionRenderer
      .getSectionHTML(this.dataset.sectionId, false, url)
      .then((resultsMarkup) => {
        if (!resultsMarkup) return;

        if (abortController.signal.aborted) return;

        morph(predictiveSearchResults, resultsMarkup);
        publishSearchUsed('search_modal', searchTerm);

        const firstQueryPill = predictiveSearchResults.querySelector(
          '.predictive-search-results__card--query [aria-label]'
        );
        this.#lastQuerySuggestion = firstQueryPill?.getAttribute('aria-label') || null;
        this.#updateGhostText(this.refs.searchInput.value.trim());

        this.#resetScrollPositions();
      })
      .catch((error) => {
        if (abortController.signal.aborted) return;
        throw error;
      });
  }

  /**
   * Fetch the markup for the recently viewed products.
   * @returns {Promise<string | null>} The markup for the recently viewed products.
   */
  async #getRecentlyViewedProductsMarkup() {
    if (!this.dataset.sectionId) return null;

    const viewedProducts = RecentlyViewed.getProducts();
    if (viewedProducts.length === 0) return null;

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', viewedProducts.map(/** @param {string} id */ (id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');

    return sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
  }

  #hideResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = true;
  }

  #showResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = false;
  }

  #createAbortController() {
    const abortController = new AbortController();
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
    this.#activeFetch = abortController;
    return abortController;
  }

  #resetSearch = async () => {
    const { predictiveSearchResults, searchInput } = this.refs;
    const emptySectionId = 'predictive-search-empty';

    this.#currentIndex = -1;
    searchInput.value = '';
    this.#lastQuerySuggestion = null;
    this.#updateGhostText('');
    this.#hideResetButton();

    const abortController = this.#createAbortController();
    const url = new URL(window.location.href);
    url.searchParams.delete('page');

    const emptySectionMarkup = await sectionRenderer.getSectionHTML(emptySectionId, false, url);
    const parsedEmptySectionMarkup = new DOMParser()
      .parseFromString(emptySectionMarkup, 'text/html')
      .querySelector('.predictive-search-empty-section');

    if (!parsedEmptySectionMarkup) throw new Error('No empty section markup found');

    /** This needs to be awaited and not .then so the DOM is already morphed
     * when #closeResults is called and therefore the height is animated */
    const viewedProducts = RecentlyViewed.getProducts();

    if (viewedProducts.length > 0) {
      const recentlyViewedMarkup = await this.#getRecentlyViewedProductsMarkup();
      if (!recentlyViewedMarkup) return;

      const parsedRecentlyViewedMarkup = new DOMParser().parseFromString(recentlyViewedMarkup, 'text/html');
      const recentlyViewedProductsHtml = parsedRecentlyViewedMarkup.getElementById('predictive-search-products');
      if (!recentlyViewedProductsHtml) return;

      for (const child of recentlyViewedProductsHtml.children) {
        if (child instanceof HTMLElement) {
          child.setAttribute('ref', 'recentlyViewedWrapper');
        }
      }

      const collectionElement = parsedEmptySectionMarkup.querySelector('#predictive-search-products');
      if (!collectionElement) return;
      collectionElement.prepend(...recentlyViewedProductsHtml.children);
    }

    if (abortController.signal.aborted) return;

    morph(predictiveSearchResults, parsedEmptySectionMarkup);
    this.#resetScrollPositions();
  };
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}
